import React from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { FONT } from './bogoShared';

export const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const num = (v) => Number(v) || 0;

// Same rule the shop and the cart use: every product must be active and have enough units for one combo.
export const stockInfo = (p, qty = 1) => {
  const need = qty > 0 ? qty : 1;
  const stock = typeof p?.availableQuantity === 'number' ? p.availableQuantity : 0;
  const makes = Math.floor(stock / need);
  if (!p || p.isDeleted) return { state: 'out', label: 'Removed', stock, makes: 0, why: 'This product was deleted' };
  if (p.status === 'inactive') return { state: 'out', label: 'Inactive', stock, makes: 0, why: 'This product is switched off' };
  if (p.ownership === 'Seller Product') return { state: 'ok', label: 'Seller stock', stock, makes: Infinity, why: 'Seller product: the seller supplies it when an order is placed, so it is never out of stock' };
  if (p.inStock === false) return { state: 'out', label: 'Out of stock', stock, makes: 0, why: 'Marked out of stock on the product' };
  if (stock <= 0) return { state: 'out', label: 'Out of stock', stock, makes: 0, why: 'No units available' };
  if (makes <= 0) return { state: 'out', label: 'Not enough', stock, makes: 0, why: `Has ${stock}, one combo needs ${need}` };
  if (makes <= 3) return { state: 'low', label: 'Low stock', stock, makes, why: `Only enough for ${makes} combo${makes === 1 ? '' : 's'}` };
  return { state: 'ok', label: 'Available', stock, makes, why: `Enough for ${makes} combos` };
};

const TONE = {
  ok: { bg: '#dcfce7', fg: '#166534' },
  low: { bg: '#fef3c7', fg: '#92400e' },
  out: { bg: '#fee2e2', fg: '#991b1b' },
};

export const isSeller = (p) => p?.ownership === 'Seller Product';

// Own and seller products in one combo is allowed, but worth a heads-up.
export const mixOf = (products) => {
  const list = products.filter(Boolean);
  const seller = list.filter(isSeller);
  const own = list.filter((p) => !isSeller(p));
  return { mixed: seller.length > 0 && own.length > 0, seller, own };
};

export const MixNotice = ({ products, sx }) => {
  const m = mixOf(products);
  if (!m.mixed) return null;
  return (
    <Alert severity="warning" sx={sx}>
      <b>This combo mixes own and seller products.</b> Seller products ({m.seller.map((p) => p.title).join(', ')}) are never counted as out of stock, so the combo only goes out of stock when an own product runs out.
      When a customer orders it, a seller order is created automatically for each seller product, and the seller ships it to the warehouse. You can still save it.
    </Alert>
  );
};

export const StockChip = ({ info }) => (
  <Chip size="small" label={info.label} title={info.why} sx={{ fontFamily: FONT, fontWeight: 800, height: 22, background: TONE[info.state].bg, color: TONE[info.state].fg }} />
);

// lines: [{ product, quantity }]
export const totalsFor = (lines) => {
  const side = (mrpKey, sellKey) => lines.reduce((a, l) => {
    const q = l.quantity || 1;
    const mrp = num(l.product?.[mrpKey]);
    const sell = num(l.product?.[sellKey]) || mrp;
    return { mrp: a.mrp + mrp * q, separately: a.separately + sell * q };
  }, { mrp: 0, separately: 0 });
  return { b2c: side('b2c_mrp', 'b2c_sellingPrice'), b2b: side('b2c_mrp', 'b2b_sellingPrice') };
};

const th = { fontFamily: FONT, fontSize: 11.5, fontWeight: 800, color: '#64748b', textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' };
const td = { fontFamily: FONT, fontSize: 13, textAlign: 'right', padding: '9px 10px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };

// Per-product MRP and selling price for one audience, then the combo price against both totals.
const Side = ({ title, accent, lines, mrpKey, sellKey, totals, comboPrice }) => {
  const save = comboPrice > 0 && totals.separately > comboPrice ? totals.separately - comboPrice : 0;
  const saveMrp = comboPrice > 0 && totals.mrp > comboPrice ? totals.mrp - comboPrice : 0;
  const pct = totals.mrp > 0 && saveMrp ? Math.round((saveMrp / totals.mrp) * 100) : 0;
  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '12px', overflow: 'hidden', minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, p: 1.5, pb: 0.5 }}>{title}</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Product</th>
              <th style={th}>Qty</th>
              <th style={th}>MRP</th>
              <th style={th}>Selling</th>
              <th style={th}>MRP × Qty</th>
              <th style={th}>Selling × Qty</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const q = l.quantity || 1;
              const mrp = num(l.product?.[mrpKey]);
              const rawSell = num(l.product?.[sellKey]);
              const sell = rawSell || mrp;
              return (
                <tr key={l.product?._id || i}>
                  <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal', minWidth: 140, fontWeight: 700 }}>{l.product?.title || 'Removed product'}</td>
                  <td style={td}>{q}</td>
                  <td style={{ ...td, color: mrp ? '#0f172a' : '#dc2626' }}>{mrp ? inr(mrp) : 'Not set'}</td>
                  <td style={{ ...td, color: rawSell ? '#0f172a' : '#94a3b8' }} title={rawSell ? '' : 'No selling price, MRP is used'}>{rawSell ? inr(rawSell) : `${inr(sell)}*`}</td>
                  <td style={td}>{inr(mrp * q)}</td>
                  <td style={{ ...td, fontWeight: 800 }}>{inr(sell * q)}</td>
                </tr>
              );
            })}
            <tr>
              <td style={{ ...td, textAlign: 'left', fontWeight: 800, background: '#f8fafc' }} colSpan={4}>Total</td>
              <td style={{ ...td, fontWeight: 800, background: '#f8fafc' }}>{inr(totals.mrp)}</td>
              <td style={{ ...td, fontWeight: 800, background: '#f8fafc' }}>{inr(totals.separately)}</td>
            </tr>
          </tbody>
        </table>
      </Box>
      <Box sx={{ p: 1.5, display: 'grid', gap: 0.75 }}>
        {[
          ['Total MRP', inr(totals.mrp)],
          ['Bought separately', inr(totals.separately)],
          ['Combo price', comboPrice > 0 ? inr(comboPrice) : 'Not set'],
          ['Saves vs bought separately', comboPrice > 0 ? (save ? inr(save) : 'No saving') : '—'],
          ['Saves vs MRP', comboPrice > 0 ? (saveMrp ? `${inr(saveMrp)} (${pct}%)` : 'No saving') : '—'],
        ].map(([k, v], i) => (
          <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontFamily: FONT, fontSize: 13, fontWeight: i === 2 ? 800 : 600, color: i === 2 ? '#0f172a' : '#475569' }}>
            <span>{k}</span><span style={{ color: i >= 3 && v !== 'No saving' && v !== '—' ? '#166534' : undefined }}>{v}</span>
          </Box>
        ))}
        {comboPrice > 0 && totals.separately > 0 && comboPrice > totals.separately && (
          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#b45309', fontWeight: 700 }}>Costs more than buying the products separately.</Typography>
        )}
      </Box>
    </Box>
  );
};

export const PriceBreakdown = ({ lines, b2cPrice, b2bPrice }) => {
  const t = totalsFor(lines);
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'repeat(2, minmax(0, 1fr))' } }}>
      <Side title="B2C — customers" accent="#6366f1" lines={lines} mrpKey="b2c_mrp" sellKey="b2c_sellingPrice" totals={t.b2c} comboPrice={num(b2cPrice)} />
      <Side title="B2B — business buyers" accent="#0ea5e9" lines={lines} mrpKey="b2c_mrp" sellKey="b2b_sellingPrice" totals={t.b2b} comboPrice={num(b2bPrice)} />
      <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b', gridColumn: '1 / -1' }}>
        * No selling price on the product, so its MRP is counted. The MRP is the same for B2C and B2B; only the selling price differs. MRP is what the product is printed at; selling is what it costs on its own today.
      </Typography>
    </Box>
  );
};
